<template>
  <div class="RendererPdf">
    <UISpinner
      v-if="loading || isOpening"
      size="md"
      class="RendererPdf__loader"
    />
    <div v-else-if="displayError" class="RendererPdf__error">
      {{ displayError }}
    </div>
    <div v-else ref="scrollRef" class="RendererPdf__scroll">
      <canvas
        v-for="page in pages"
        :key="page.pageNumber"
        :ref="(element) => registerCanvas(page.pageNumber, element)"
        class="RendererPdf__page"
        :style="{ aspectRatio: page.aspectRatio }"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  type ComponentPublicInstance,
  computed,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue'

import { UISpinner } from '@foundever/ui-lib'

import { loadPdfjs } from '@/utils/pdfjs'

import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  RenderTask,
} from 'pdfjs-dist'

export interface Props {
  /** Unused: the preview panel passes its text content to every renderer. */
  content?: string
  /** File name, shown by the panel header; kept for the shared renderer contract. */
  title: string
  /** PDF bytes; the renderer copies them, so the stored buffer is never detached. */
  buffer: ArrayBuffer | null
  /** Bytes are still being fetched: shows the loader. */
  loading?: boolean
  /** Fetch failure from the panel: shown in place of the pages. */
  errorMessage?: string | null
  /** Split-pane drag in progress: pages stretch by CSS and re-render once it ends. */
  isResizing?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  content: '',
  loading: false,
  errorMessage: null,
  isResizing: false,
})

/** Caps canvas resolution: a retina page at 3x costs more memory than it shows. */
const MAX_PIXEL_RATIO = 2
/** Pages this far outside the scroll box (as a share of its height) render ahead of time. */
const RENDER_AHEAD_MARGIN = '100% 0px'
const RESIZE_SETTLE_MS = 150

const scrollRef = ref<HTMLElement | null>(null)
const isOpening = ref(false)
const localError = ref<string | null>(null)
const displayError = computed(() => props.errorMessage || localError.value)

// #region Document
/** Page boxes start with the first page's ratio and refine as each page renders. */
const pages = ref<Array<{ pageNumber: number; aspectRatio: string }>>([])

let loadToken = 0
let loadingTask: PDFDocumentLoadingTask | null = null
let pdfDocument: PDFDocumentProxy | null = null

async function openDocument(buffer: ArrayBuffer): Promise<void> {
  const token = ++loadToken
  closeDocument()
  localError.value = null
  isOpening.value = true
  try {
    const pdfjs = await loadPdfjs()
    if (token !== loadToken) return
    // pdfjs transfers the bytes to its worker, detaching them; the stored buffer has
    // to survive for a later re-open of the same preview.
    const task = pdfjs.getDocument({ data: new Uint8Array(buffer.slice(0)) })
    loadingTask = task
    const document = await task.promise
    const firstPage = await document.getPage(1)
    const { width, height } = firstPage.getViewport({ scale: 1 })
    const defaultAspectRatio = `${width} / ${height}`
    const pageBoxes = Array.from({ length: document.numPages }, (_, index) => ({
      pageNumber: index + 1,
      aspectRatio: defaultAspectRatio,
    }))
    if (token !== loadToken) return
    pdfDocument = document
    pages.value = pageBoxes
  } catch (error) {
    if (token !== loadToken) return
    localError.value =
      error instanceof Error ? error.message : 'Could not render PDF preview'
  } finally {
    if (token === loadToken) isOpening.value = false
  }
}

function closeDocument(): void {
  renderTaskByPage.forEach((task) => task.cancel())
  renderTaskByPage.clear()
  renderedWidthByPage.clear()
  visiblePages.clear()
  pages.value = []
  pdfDocument = null
  // Destroying the loading task destroys its document too.
  void loadingTask?.destroy()
  loadingTask = null
}

// #endregion

// #region Page rendering
const canvasByPage = new Map<number, HTMLCanvasElement>()
const pageByCanvas = new WeakMap<Element, number>()
const renderTaskByPage = new Map<number, RenderTask>()
/** CSS width each page was last drawn at; a page is redrawn only when that changes. */
const renderedWidthByPage = new Map<number, number>()
const visiblePages = new Set<number>()

let intersectionObserver: IntersectionObserver | null = null

function registerCanvas(
  pageNumber: number,
  element: Element | ComponentPublicInstance | null,
): void {
  const previous = canvasByPage.get(pageNumber)
  if (previous && previous !== element) {
    intersectionObserver?.unobserve(previous)
    canvasByPage.delete(pageNumber)
  }
  if (!(element instanceof HTMLCanvasElement) || previous === element) return
  canvasByPage.set(pageNumber, element)
  pageByCanvas.set(element, pageNumber)
  intersectionObserver?.observe(element)
}

function onPagesIntersect(entries: IntersectionObserverEntry[]): void {
  for (const entry of entries) {
    const pageNumber = pageByCanvas.get(entry.target)
    if (pageNumber === undefined) continue
    if (entry.isIntersecting) {
      visiblePages.add(pageNumber)
      void renderPage(pageNumber)
    } else {
      visiblePages.delete(pageNumber)
    }
  }
}

async function renderPage(pageNumber: number): Promise<void> {
  const document = pdfDocument
  const canvas = canvasByPage.get(pageNumber)
  if (!document || !canvas || props.isResizing) return
  const cssWidth = canvas.clientWidth
  if (!cssWidth || renderedWidthByPage.get(pageNumber) === cssWidth) return
  renderedWidthByPage.set(pageNumber, cssWidth)
  renderTaskByPage.get(pageNumber)?.cancel()

  const token = loadToken
  let task: RenderTask | null = null
  try {
    const page = await document.getPage(pageNumber)
    if (token !== loadToken) return
    const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO)
    const baseViewport = page.getViewport({ scale: 1 })
    const pageBox = pages.value[pageNumber - 1]
    const aspectRatio = `${baseViewport.width} / ${baseViewport.height}`
    if (pageBox && pageBox.aspectRatio !== aspectRatio) {
      pageBox.aspectRatio = aspectRatio
    }
    const scale = (cssWidth / baseViewport.width) * pixelRatio
    const viewport = page.getViewport({ scale })
    canvas.width = Math.floor(viewport.width)
    canvas.height = Math.floor(viewport.height)
    task = page.render({ canvas, viewport })
    renderTaskByPage.set(pageNumber, task)
    await task.promise
  } catch (error) {
    if (error instanceof Error && error.name === 'RenderingCancelledException')
      return
    renderedWidthByPage.delete(pageNumber)
    if (token !== loadToken) return
    localError.value =
      error instanceof Error ? error.message : 'Could not render PDF preview'
  } finally {
    if (task && renderTaskByPage.get(pageNumber) === task) {
      renderTaskByPage.delete(pageNumber)
    }
  }
}

function rerenderVisiblePages(): void {
  renderedWidthByPage.clear()
  visiblePages.forEach((pageNumber) => void renderPage(pageNumber))
}

let resizeObserver: ResizeObserver | null = null
let resizeSettleTimer: ReturnType<typeof setTimeout> | null = null

function onScrollBoxResize(): void {
  if (resizeSettleTimer) clearTimeout(resizeSettleTimer)
  resizeSettleTimer = setTimeout(rerenderVisiblePages, RESIZE_SETTLE_MS)
}

function disconnectObservers(): void {
  intersectionObserver?.disconnect()
  intersectionObserver = null
  resizeObserver?.disconnect()
  resizeObserver = null
  if (resizeSettleTimer) clearTimeout(resizeSettleTimer)
  resizeSettleTimer = null
}

// The scroll box remounts whenever the loader or an error replaces it, so the
// observers follow the element rather than the component.
watch(
  scrollRef,
  (scrollElement) => {
    disconnectObservers()
    if (!scrollElement) return
    intersectionObserver = new IntersectionObserver(onPagesIntersect, {
      root: scrollElement,
      rootMargin: RENDER_AHEAD_MARGIN,
    })
    canvasByPage.forEach((canvas) => intersectionObserver?.observe(canvas))
    resizeObserver = new ResizeObserver(onScrollBoxResize)
    resizeObserver.observe(scrollElement)
  },
  { flush: 'post' },
)

watch(
  () => props.isResizing,
  (isResizing) => {
    if (!isResizing) rerenderVisiblePages()
  },
)
// #endregion

// #region Lifecycle
// Last in setup: `immediate` runs it now, and both branches reach the page-rendering
// state declared above.
watch(
  () => [props.buffer, props.loading, props.errorMessage] as const,
  ([buffer, isLoading, error]) => {
    if (!buffer || isLoading || error) {
      loadToken += 1
      closeDocument()
      isOpening.value = false
      localError.value = null
      return
    }
    void openDocument(buffer)
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  loadToken += 1
  disconnectObservers()
  closeDocument()
})
// #endregion
</script>

<style scoped lang="scss">
.RendererPdf {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;

  .RendererPdf__loader {
    margin: auto;
  }

  .RendererPdf__error {
    @include font-body-small-regular;
    padding: 1rem 0;
    color: var(--text-status-error, #850305);
  }

  .RendererPdf__scroll {
    @include customScrollbarInset(0.75rem);
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.75rem;
    min-height: 0;
    background: var(--bg-secondary, #f2f2f3);
  }

  .RendererPdf__page {
    display: block;
    flex-shrink: 0;
    width: 100%;
    height: auto;
    border: 1px solid var(--border-light, #f2f2f3);
    background: var(--bg-primary, #fff);
  }
}
</style>
